/**
 * IMPORTS
 */
import {remove as removeConversation} from 'src/aggregates/conversations/api';
import {fetchMessageByOriginalId as getMessageByOriginalId}
    from 'src/aggregates/conversations/utils';
import {sendTemplate as timelineSendTemplate}
    from 'src/aggregates/conversations/utils';
import customersSelectors from 'src/aggregates/customers/selectors';
import {timelineAdded} from 'src/aggregates/timelines/events';
import {timelineAllContactMessagesLoaded}
    from 'src/aggregates/timelines/events';
import {timelineByConversationCleared} from 'src/aggregates/timelines/events';
import {timelineByConversationLoaded} from 'src/aggregates/timelines/events';
import {timelineByConversationMessagesLoaded}
    from 'src/aggregates/timelines/events';
import {timelineByConversationMessagesLoading}
    from 'src/aggregates/timelines/events';
import {timelineByConversationMessagesNotLoaded}
    from 'src/aggregates/timelines/events';
import {timelineByConversationRemoved} from 'src/aggregates/timelines/events';
import {timelineErrorReset} from 'src/aggregates/timelines/events';
import {timelineFocused} from 'src/aggregates/timelines/events';
import {timelineLoaded} from 'src/aggregates/timelines/events';
import {timelineLoading} from 'src/aggregates/timelines/events';
import {timelineMessageAckReceived} from 'src/aggregates/timelines/events';
import {timelineMessageByOriginalIdFetched}
    from 'src/aggregates/timelines/events';
import {timelineMessageByOriginalIdFetching}
    from 'src/aggregates/timelines/events';
import {timelineMessageByOriginalIdNotFetched}
    from 'src/aggregates/timelines/events';
import {timelineMessageOriginalIdReceived}
    from 'src/aggregates/timelines/events';
import {timelineMessagesLoaded} from 'src/aggregates/timelines/events';
import {timelineMessagesLoading} from 'src/aggregates/timelines/events';
import {timelineMessagesNotLoaded} from 'src/aggregates/timelines/events';
import {timelineNotLoaded} from 'src/aggregates/timelines/events';
import {timelineRemoved} from 'src/aggregates/timelines/events';
import {timelineTemplateSending} from 'src/aggregates/timelines/events';
import {timelineTemplateSent} from 'src/aggregates/timelines/events';
import {timelineTemplateNotSent} from 'src/aggregates/timelines/events';
import * as timelineSelectors from 'src/aggregates/timelines/selectors';
import {getContactTimeline} from 'src/aggregates/timelines/utils';
import {getConversation} from 'src/aggregates/timelines/utils';
import {getMessages} from 'src/aggregates/timelines/utils';
import * as user from 'src/aggregates/user/selectors';
import {reducePayload} from 'src/aggregates/utils/payloads';
import {showUnavailableConversationPopup} from 'src/utils/popup';
import uuid4 from 'uuid4';


/**
 * TYPES
 */
import {AckTypes} from 'src/aggregates/conversations/message/messages.d';
import {MessageTypes} from 'src/aggregates/conversations/message/messages.d';
import {IAppState} from 'src/aggregates/index.d';
import {IEvent} from 'src/aggregates/index.d';
import {ITimelineAdd} from 'src/aggregates/timelines/commands.d';
import {ITimelineClearByConversation}
    from 'src/aggregates/timelines/commands.d';
import {ITimelineFetch} from 'src/aggregates/timelines/commands.d';
import {ITimelineFetchMessageByOriginalId}
    from 'src/aggregates/timelines/commands.d';
import {ITimelineFocus} from 'src/aggregates/timelines/commands.d';
import {ITimelineLoadByContact} from 'src/aggregates/timelines/commands.d';
import {ITimelineLoadNextConversationMessages}
    from 'src/aggregates/timelines/commands.d';
import {ITimelineLoadNextMessagesByConversation}
    from 'src/aggregates/timelines/commands.d';
import {ITimelineOnMessageAck} from 'src/aggregates/timelines/commands.d';
import {ITimelineRemove} from 'src/aggregates/timelines/commands.d';
import {ITimelineResetError} from 'src/aggregates/timelines/commands.d';
import {ITimelineSendTemplate} from 'src/aggregates/timelines/commands.d';
import {ITimeline} from 'src/aggregates/timelines/handlers.d';
import {IMessage} from 'src/aggregates/timelines/state.d';


/**
 * CODE
 */

/**
 * I am a timeline add command handler.
 *
 * :param command: add command
 * :param state: application state
 *
 * :generates: events
 */
async function* add (
    command: ITimelineAdd,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get conversation to be added
    const conversation = timelineSelectors.getConversation(command.id);

    // get timeline by conversation
    const timeline = timelineSelectors.getByConversation(command.id);

    // remove timeline by conversation
    yield timelineByConversationRemoved(command.id);

    // conversation does not exist and its channel is focused: add it
    if (conversation === null &&
        state.conversation.currentAccount === command.account &&
        state.conversation.currentContact === command.contact)
    {
        // timeline already loaded: generate timeline loaded event
        if (timeline !== null)
        {
            yield timelineLoaded(timeline);
        }

        // generate timeline added event
        yield timelineAdded(
            command.account,
            command.application,
            command.end,
            command.id,
            command.lastInteraction,
            command.remote,
            command.start,
            command.status,
            command.subject,
        );

        // add each messages to timeline event
        yield timelineMessagesLoaded(command.id, command.messages);

        // added conversation was current one: focus conversation
        if (state.conversation.current === command.id)
        {
            yield timelineFocused(command.id);
        }
    }
}


/*
 * I am a timeline clear by conversation command handler.
 *
 * :param command: timeline clear by conversation command
 * :param state: application state
 *
 * :generates: events
 */
async function* clearByConversation(
    command: ITimelineClearByConversation,
    state: IAppState,
): AsyncIterable<IEvent>
{
    yield timelineByConversationCleared();
}


/*
 * I am a timeline fetch command handler.
 *
 * :param command: timeline fetch command
 * :param state: application state
 *
 * :generates: events
 */
async function* fetch (
    command: ITimelineFetch,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get selected conversation on timeline array
    const conversation = getConversation(command._target);

    // requested conversation not found or already loaded its messages: return
    if (conversation === undefined || conversation.messages !== undefined)
    {
        return;
    }

    // generate loading messages event
    yield timelineMessagesLoading(command._target);

    // fetch messages
    const messages = await getMessages(state.user.company, command._target);

    // load messages failed: generate messages not loaded event and stop
    if (messages === null)
    {
        yield timelineMessagesNotLoaded(command._target);
        return;
    }

    // initialize list of reduced messages
    const reduced : IMessage[] = [];

    // load messages succeded: reduce messages
    for (const message of messages)
    {
        // initialize reduced message
        let result : IMessage;

        // reduce message
        try
        {
            result = await reducePayload(message);
            result.timestamp *= 1000;
        }

        // reduce failed: abort
        catch (e)
        {
            yield timelineMessagesNotLoaded(command._target);
            return;
        }

        // reduce failed: abort
        if (result === null)
        {
            yield timelineMessagesNotLoaded(command._target);
            return;
        }

        // add reduced message to list
        reduced.push({
            ...result,
            conversation: command._target,
        });
    }

    // generate messages loaded event
    yield timelineMessagesLoaded(command._target, reduced);
}


/**
 * I am a fetch message by original id command handler.
 *
 * :param command: fetch message by original id command
 * :param state: application state
 *
 * :returns: events
 */
async function* fetchMessageByOriginalId (
    command: ITimelineFetchMessageByOriginalId,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get props from command
    const {
        message: messageId,
        _target: conversationId,
        reply,
    } = command;

    // get current conversation id
    const current = state.conversation.current;

    // get conversation data
    const conversation = timelineSelectors.getConversation(conversationId) ??
                         timelineSelectors.getByConversation(current)
                             ?.find(({id}) => id === conversationId);

    // get replying message
    const message = conversation?.messages?.find(({id}) => id === messageId);

    // message already fetched: stop
    if (message?.replyFetched === true)
    {
        return [];
    }

    // generate fetching event
    yield timelineMessageByOriginalIdFetching(conversationId,
                                              messageId,
                                              current);

    // fetch message
    const data = await getMessageByOriginalId(reply);

    // fetch failed: generate not fetched event and return
    if (data === null)
    {
        yield timelineMessageByOriginalIdNotFetched(conversationId,
                                                    messageId,
                                                    current);
        return;
    }

    // generate fetched event
    yield timelineMessageByOriginalIdFetched(conversationId,
                                             messageId,
                                             current,
                                             data);
}


/**
 * I am a timeline focus command handler.
 *
 * :param command: timeline focus command
 * :param state: application state
 *
 * :generates: events
 */
async function* focus (
    command: ITimelineFocus,
    state: IAppState,
): AsyncIterable<IEvent>
{
    yield timelineFocused(command._target);
}


/**
 * I am a timeline load by contact command handler.
 *
 * :param command: load by contact command
 * :param state: application state
 *
 * :generates: events
 */
async function* loadByContact (
    command: ITimelineLoadByContact,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // unfocus timeline
    yield timelineFocused(null);

    // get current conversation
    const conversation = state.conversation.current;

    // get timeline by conversation
    const byConversation = timelineSelectors.getByConversation(conversation);

    // has conversation and timeline already loaded: return
    if (conversation !== null && byConversation !== null)
    {
        return;
    }

    // get contact
    const contact = customersSelectors.getContactById(command.contact);

    // contact is not defined: return
    if (contact === null)
    {
        return;
    }

    // generate timeline loading event
    yield timelineLoading();

    // load contact conversations on timeline
    const timeline = await getContactTimeline(
        state.conversation.currentAccount,
        state.user.company,
        contact.value,
    );

    // load contact conversations on timeline failed: generate event and stop
    if (timeline === null)
    {
        yield timelineNotLoaded();
        return;
    }

    // set reduced timeline
    const reducedTimeline: ITimeline[] = [];

    // generate an added event for each timeline finished event
    for (const event of timeline)
    {
        // get conversation application uid and type
        const application = event.application ? event.application.uid : null;

        // conversation is current or ongoing with agent or echo app: skip it
        if (event.id === conversation ||
           (event.end === null &&
           (application === user.fetch().id ||
            application?.startsWith('echo') === true)))
        {
            continue;
        }

        // valid timeline conversation: add it
        reducedTimeline.push({
            account: event.account,
            agentFinished: event.agentFinished,
            application,
            dynamic: event.dynamic,
            end: event.end,
            id: event.id,
            isSendingTemplate: false,
            lastInteraction: event.lastInteraction,
            remote: event.remote,
            start: event.start,
            status: null,
            subject: event.subject,
            templateRequired: event.templateRequired,
            templateSent: event.templateSent !== null ?
                event.templateSent * 1000 : null,
        });
    }

    // has chat conversation: generate timeline by conversation loaded event
    if (conversation !== null && contact.type_id !== 8)
    {
        yield timelineByConversationLoaded(conversation, reducedTimeline);
        return;
    }

    // generate loaded event
    yield timelineLoaded(reducedTimeline);
}


/**
 * I am a load next conversation messages command handler.
 *
 * :param command: load messages command
 * :param state: application state
 *
 * :generates: events
 */
async function* loadNextConversationMessages (
    command: ITimelineLoadNextConversationMessages,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // is loading a timeline: return
    if (state.timeline.isLoading === true)
    {
        return;
    }

    // not loading a timeline: get id of conversation that will load messages
    const conversation = timelineSelectors.getNextConversation();

    // all timeline conversations messages loaded: generate event and return
    if (conversation === null)
    {
        yield timelineAllContactMessagesLoaded();
        return;
    }

    // still have messages but is already fetching them: return
    if (conversation.isLoadingMessages === true)
    {
        return;
    }

    // not fetching new messages: generate loading messages event
    yield timelineMessagesLoading(conversation.id);

    // fetch messages
    const messages = await getMessages(state.user.company, conversation.id);

    // load messages failed: generate messages not loaded event and stop
    if (messages === null)
    {
        yield timelineMessagesNotLoaded(conversation.id);
        return;
    }

    // initialize list of reduced messages
    const reduced : IMessage[] = [];

    // load messages succeded: reduce messages
    for (const message of messages)
    {
        // initialize reduced message
        let result : IMessage;

        // reduce message
        try
        {
            result = await reducePayload(message);
            result.timestamp *= 1000;
        }

        // reduce failed: abort
        catch (e)
        {
            yield timelineMessagesNotLoaded(conversation.id);
            return;
        }

        // reduce failed: abort
        if (result === null)
        {
            yield timelineMessagesNotLoaded(conversation.id);
            return;
        }

        // add reduced message to list
        reduced.push({
            ...result,
            conversation: conversation.id,
        });
    }

    // generate messages loaded event
    yield timelineMessagesLoaded(conversation.id, reduced);
}


/**
 * I am a load next messages by conversation command handler.
 *
 * :param command: load next messages by conversation command
 * :param state: application state
 *
 * :generates: events
 */
async function* loadNextMessagesByConversation (
    command: ITimelineLoadNextMessagesByConversation,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // is loading a timeline: return
    if (state.timeline.isLoading === true)
    {
        return;
    }

    // get conversation id
    const {conversation} = command;

    // not loading a timeline: get conversation timeline that will load messages
    const timeline = timelineSelectors.getNextConversation(
        command.conversation,
    );

    // all timeline conversations messages loaded: generate event and return
    if (timeline === null)
    {
        yield timelineAllContactMessagesLoaded();
        return;
    }

    // still have messages but is already fetching them: return
    if (timeline.isLoadingMessages === true)
    {
        return;
    }

    // not fetching new messages: generate loading messages event
    yield timelineByConversationMessagesLoading(
        conversation,
        timeline.id,
    );

    // fetch messages
    const messages = await getMessages(state.user.company, timeline.id);

    // load messages failed: generate messages not loaded event and stop
    if (messages === null)
    {
        yield timelineByConversationMessagesNotLoaded(
            conversation,
            timeline.id,
        );
        return;
    }

    // initialize list of reduced messages
    const reduced : IMessage[] = [];

    // load messages succeeded: reduce messages
    for (const message of messages)
    {
        // initialize reduced message
        let result : IMessage;

        // reduce message
        try
        {
            result = await reducePayload(message);
            result.timestamp *= 1000;
        }

        // reduce failed: abort
        catch (e)
        {
            yield timelineByConversationMessagesNotLoaded(
                conversation,
                timeline.id,
            );
            return;
        }

        // reduce failed: abort
        if (result === null)
        {
            yield timelineByConversationMessagesNotLoaded(
                conversation,
                timeline.id,
            );
            return;
        }

        // add reduced message to list
        reduced.push({
            ...result,
            conversation: timeline.id,
        });
    }

    // generate messages loaded event
    yield timelineByConversationMessagesLoaded(
        conversation,
        reduced,
        timeline.id,
    );
}


/**
 * I am an on message ack timeline command handler.
 *
 * :param command: on message ack command
 * :param state: application state
 *
 * :generates: events
 */
async function* onMessageAck(
    command: ITimelineOnMessageAck,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // conversation or message does not exist: return
    if (timelineSelectors.getConversation(command._target) === null)
    {
        return;
    }

    // command has originalId: yield message original id received event
    if (command.originalId !== null)
    {
        yield timelineMessageOriginalIdReceived(command._target,
                                                command.id,
                                                command.originalId);
    }

    // yield conversation message ack received event
    yield timelineMessageAckReceived(command._target,
                                     command.id,
                                     command.ack);
}


/**
 * I am a timeline remove command handler.
 *
 * :param command: remove command
 * :param state: application state
 *
 * :generates: events
 */
async function* remove (
    command: ITimelineRemove,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get conversation to be added
    const conversation = timelineSelectors.getConversation(command._target);

    // conversation exist on timeline aggregate: remove it
    if (conversation !== null)
    {
        // conversation to be removed is the current one: unfocus it
        if (conversation.id === state.timeline.current)
        {
            yield timelineFocused(null);
        }

        // remove it
        yield timelineRemoved(command._target);
    }
}


/**
 * I am a reset error command handler.
 *
 * :generates: events
 */
async function* resetError (command: ITimelineResetError,
                            state: IAppState): AsyncIterable<IEvent>
{
    yield timelineErrorReset();
}


/**
 * I am a timeline send template command handler.
 *
 * :param command: remove command
 * :param state: application state
 *
 * :generates: events
 */
async function* sendTemplate(
    command: ITimelineSendTemplate,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get conversation id
    const {_target: conversationId, templateInput} = command;

    // generate original message id
    const originalId = conversationId ?
        `${conversationId}.${Date.now()}.${uuid4()}` : uuid4();

    // create body content from template input
    const templateBody = {
        fields: {},
        id: templateInput?.id ?? '',
        language: templateInput?.language ?? '',
        name: templateInput?.name ?? '',
        params: templateInput?.params ?? [],
        text: templateInput?.data,
    };

    // create message with template information
    const template: IMessage = {
        ack: AckTypes.TRANSMITTING,
        attachments: [],
        body: JSON.stringify(templateBody),
        caption: '',
        deleted: null,
        id: null,
        name: null,
        originalId: originalId,
        outgoing: user.fetch().id,
        reducedBody: '',
        referral: null,
        reply: null,
        timestamp: Date.now(),
        type: MessageTypes.TEMPLATE,
    };

    // generate template sending event
    yield timelineTemplateSending(conversationId, template);

    // get logged agent
    const agent = user.fetch().id;

    // send template
    const {message, reason} = await timelineSendTemplate(
        state.user.company,
        conversationId,
        agent,
        template.originalId,
        templateInput,
    );

    // send message succeeded: set message id, generate event and finish
    if (message !== undefined && message.id !== null)
    {
        yield timelineTemplateSent(
            conversationId,
            template.originalId,
            message,
        );
        return;
    }

    // send failed: generate event
    yield timelineTemplateNotSent(
        conversationId,
        template.originalId,
    );

    // conversation not with user: inform user and remove
    if (reason === 'conversationExited')
    {
        // show popup
        showUnavailableConversationPopup();

        // remove conversation
        removeConversation(conversationId);
    }
}


/**
 * EXPORTS
 */
export {
    add,
    clearByConversation,
    fetch,
    fetchMessageByOriginalId,
    focus,
    loadByContact,
    loadNextConversationMessages,
    loadNextMessagesByConversation,
    onMessageAck,
    remove,
    resetError,
    sendTemplate,
};
